/**
 * Filters out null parameters from the original object and returns a new object.
 *
 * @param originalObject - The original object to filter.
 * @param filterOutKeys - An optional array of keys to exclude from the filtering process.
 * @returns The filtered object without null parameters.
 */
export const filterOutNullParams = (
  originalObject?: Record<string, unknown> | undefined | null,
  filterOutKeys?: string[]
): Record<string, unknown> | null => {
  if (!originalObject) {
    return null;
  }

  const finalObject = Object.entries(originalObject).reduce(
    (acc: Record<string, unknown>, [key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        !filterOutKeys?.includes(key)
      ) {
        if (
          typeof value === 'object' &&
          !Array.isArray(value) &&
          value !== null
        ) {
          // Recursively filter the nested object
          const nestedFiltered = filterOutNullParams(
            value as Record<string, unknown>,
            filterOutKeys
          );
          if (nestedFiltered && Object.keys(nestedFiltered).length > 0) {
            acc[key] = nestedFiltered;
          }
        } else {
          acc[key] = value;
        }
      }
      return acc;
    },
    {}
  );

  return Object.keys(finalObject).length > 0 ? finalObject : null;
};

/**
 * Filters the updatable protocol parameters based on the original object,
 * reference object, and optional filter-out keys.
 *
 * @param originalObject - The original object containing the protocol parameters.
 * @param referenceObject - The reference object containing the updated protocol parameters.
 * @param filterOutKeys - An optional array of keys to be excluded from the final object.
 * @returns The filtered object containing the updatable protocol parameters or null.
 */
export const filterUpdatableProtocolParams = (
  originalObject?: Record<string, unknown> | null,
  referenceObject?: Record<string, unknown> | null,
  filterOutKeys?: string[]
): Record<string, unknown> | null => {
  if (!originalObject || !referenceObject) {
    return null;
  }

  const finalObject = Object.entries(referenceObject).reduce<
    Record<string, unknown>
  >((acc, [key, referenceValue]) => {
    const originalValue = originalObject[key];

    const isValid =
      !filterOutKeys?.includes(key) &&
      originalObject.hasOwnProperty(key) &&
      referenceValue !== undefined &&
      referenceValue !== null;

    if (isValid) {
      if (
        typeof originalValue === 'object' &&
        originalValue !== null &&
        typeof referenceValue === 'object' &&
        referenceValue !== null
      ) {
        const nestedFiltered = filterUpdatableProtocolParams(
          originalValue as Record<string, unknown>,
          referenceValue as Record<string, unknown>,
          filterOutKeys
        );
        if (nestedFiltered && Object.keys(nestedFiltered).length > 0) {
          acc[key] = nestedFiltered;
        }
      } else {
        acc[key] = originalValue;
      }
    }

    return acc;
  }, {});

  return Object.keys(finalObject).length > 0 ? finalObject : null;
};

/**
 * Maps an object to a new object by including only the desired keys
 * and converting arrays to objects.
 * @param obj - The object to map.
 * @param desiredKeys - An array of keys to include in the mapped object.
 * @returns The mapped object.
 */
export const mapArrayToObjectByKeys = (
  obj?: Record<string, unknown> | null,
  desiredKeys?: string[]
): Record<string, unknown> | null => {
  if (!obj || !desiredKeys) {
    return null;
  }

  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (desiredKeys.includes(key) && Array.isArray(value)) {
        const arrayToObject = value.reduce<Record<string, unknown>>(
          (arrayAcc, arrayValue, index) => {
            arrayAcc[index] = arrayValue;
            return arrayAcc;
          },
          {}
        );
        acc[key] = arrayToObject;
      } else if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        acc[key] = mapArrayToObjectByKeys(
          value as Record<string, unknown>,
          desiredKeys
        );
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );
};
